import { useMemo } from 'react';
import { Utility } from '@nkr/services/utility';
import { ISurgicalDecline } from '@nkr/models/index';
import { ColGroupDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams, SortChangedEvent, ValueGetterParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react'
import { GridWrapper } from './styles'
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: ISurgicalDecline[],
    onGridReady: (e: GridReadyEvent) => void,
}

const Grid = (props: IProps) => {
    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onSortChanged = (e: SortChangedEvent) => {
        e.api.refreshCells();
    };

    const cols: ColGroupDef[] = [
        {
            groupId: 'first',
            headerClass: 'group-header',
            children: [
                {
                    colId: 'Rank',
                    field: 'rank',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            R#
                        </SortableHeaderComponent>,
                    headerName: 'R#',
                    width: 55,
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    sortable: false,
                    suppressSizeToFit: true,
                    valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
                },
                {
                    colId: 'outcome',
                    field: 'outcome',
                    headerName: 'Outcome',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    width: 95,
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Outcome
                        </SortableHeaderComponent>,
                },
                {
                    colId: 'declineDate',
                    field: 'declineDate',
                    headerName: 'Outcome Date',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Outcome Date
                        </SortableHeaderComponent>,
                    width: 100,
                    comparator: Utility.dateComparator
                },
                {
                    colId: 'targetSurgeryDate',
                    field: 'targetSurgeryDate',
                    headerName: 'Target Surgery Date',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Target Surgery Date
                        </SortableHeaderComponent>,
                    width: 120,
                    comparator: Utility.dateComparator
                },
            ]
        },
        {
            groupId: 'recipient',
            headerClass: 'group-header',
            headerGroupComponentFramework: (params: IHeaderParams) => <div>Recipient</div>,
            children: [
                {
                    colId: 'recipientAlias',
                    field: 'recipientAlias',
                    headerName: 'Recipient Alias',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Alias
                        </SortableHeaderComponent>,
                    width: 110,
                },
                {
                    colId: 'recipientBloodType',
                    field: 'recipientBloodType',
                    headerName: 'Recipient ABO',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    width: 70,
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            ABO
                        </SortableHeaderComponent>,
                },
                {
                    colId: 'recipientAge',
                    field: 'recipientAge',
                    headerName: 'Recipient Age',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Age
                        </SortableHeaderComponent>,
                    width: 70,
                },
                {
                    colId: 'cPRA',
                    field: 'cPRA',
                    headerName: 'Recipient NKR cPRA',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            NKR cPRA
                        </SortableHeaderComponent>,
                    valueFormatter: (params) => params.value != null ? (+params.value).toFixed(1) : '',
                    width: 80,
                },
                {
                    colId: 'patientType',
                    field: 'patientType',
                    headerName: 'Recipient P Type',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            P Type
                        </SortableHeaderComponent>,
                    width: 80,
                },
                {
                    colId: 'wt',
                    field: 'wt',
                    headerName: 'Recipient Wait Time',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Wait Time
                        </SortableHeaderComponent>,
                    width: 70,
                },
            ]
        },
        {
            groupId: 'donor',
            headerName: 'Donor',
            headerClass: 'group-header',
            headerGroupComponentFramework: (params: IHeaderParams) => <div>Donor</div>,
            children: [
                {
                    colId: 'donorAlias',
                    field: 'donorAlias',
                    headerName: 'Donor Alias',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Alias
                        </SortableHeaderComponent>,
                    width: 100,
                },
                {
                    colId: 'donorCenterAlias',
                    field: 'donorCenterAlias',
                    headerName: 'Donor Center',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Center
                        </SortableHeaderComponent>,
                    width: 100,
                },
                {
                    colId: 'donorAge',
                    field: 'donorAge',
                    headerName: 'Donor Age',
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Age
                        </SortableHeaderComponent>,
                    width: 80,
                },
            ]
        },
        {
            groupId: 'first',
            headerClass: 'group-header',
            children: [
                {
                    colId: 'status',
                    field: 'status',
                    headerName: 'Status',
                    autoHeight: true,
                    cellStyle: {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '1.5',
                    },
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    width: 150,
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Status
                        </SortableHeaderComponent>,
                },
                {
                    colId: 'reasonUnavailable',
                    field: 'reasonUnavailable',
                    headerName: 'Reason Unavailable',
                    autoHeight: true,
                    cellStyle: {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: '1.5',
                    },
                    cellClass: 'list-cell',
                    headerClass: 'list-header',
                    width: 170,
                    headerComponentFramework: (props: IHeaderParams) =>
                        <SortableHeaderComponent {...props}>
                            Reason Unavailable
                        </SortableHeaderComponent>,
                    cellRendererFramework: (props: ICellRendererParams) =>
                        props.data.reasonUnavailable ?? ''
                }
            ]
        }
    ];

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id='grid-wrapper' className='container-fluid-max-sm ag-theme-bootstrap'>
            <AgGridReact
                defaultColDef={{
                    sortable: true,
                    resizable: true,
                }}
                columnDefs={columnDefs}
                rowData={rowData}
                enableCellTextSelection={true}
                enableRangeSelection={true}
                suppressCellSelection={true}
                noRowsOverlayComponentFramework={() => <div>No data for the selected date range...</div>}
                onGridReady={props.onGridReady}
                onFirstDataRendered={onFirstDataRendered}
                onSortChanged={onSortChanged}
            />
        </GridWrapper>
    );
}

export default Grid;
